import { escapeHtml as escape } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
/**
 * Rex Mortis — The Shoutbox (global chat)
 *
 * Auto-refreshes every 10s. Post box at bottom — disabled if email not
 * verified (server enforces it too; we just want a friendlier UX).
 */
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { titledName } from '../components/Title.js';

const CHANNEL = 'global';
const REFRESH_MS = 10_000;

type Message = Awaited<ReturnType<typeof api.listShouts>>['messages'][number];

interface Flash {
  kind: 'success' | 'danger';
  message: string;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export async function renderShoutbox(root: HTMLElement): Promise<void> {
  let messages: Message[] = [];
  let flash: Flash | null = null;
  let posting = false;
  let pollTimer: number | null = null;

  async function refresh(): Promise<void> {
    try {
      const r = await api.listShouts(CHANNEL, 80);
      messages = r.messages;
    } catch (err) {
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not load messages.' };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const verified = store.get().account?.emailVerified ?? false;

    // The messages list, as standalone HTML. Used by both the full render and
    // the lightweight poll so the two never drift.
    const streamHtml = (): string =>
      messages.length === 0
        ? '<p class="caption">No shouts yet. Be the first.</p>'
        : messages
            .map(
              (m) => `
                <div style="line-height: 1.4;">
                  <span class="caption" style="font-size: 0.8rem;">${fmtTime(m.createdAt)}</span>
                  <strong style="margin-left: 0.4rem;"><a href="#/u/${encodeURIComponent(m.characterId)}">${escape(titledName(m.characterName, m.titleSlug))}</a>:</strong>
                  <span style="margin-left: 0.3rem;">${escape(m.body)}</span>
                </div>
              `,
            )
            .join('');

    // Refresh ONLY the shouts container — never the whole page — so a poll
    // can't wipe what someone is typing in the post box. Keeps the scroll
    // pinned to the bottom only if the reader was already there.
    const renderStream = (): void => {
      const stream = main.querySelector<HTMLDivElement>('#shoutbox-stream');
      if (!stream) { anchored(renderMain); return; } // structure missing — full rebuild
      const atBottom =
        stream.scrollHeight - stream.scrollTop - stream.clientHeight < 40;
      stream.innerHTML = streamHtml();
      if (atBottom) stream.scrollTop = stream.scrollHeight;
    };

    const renderMain = (): void => {
      main.innerHTML = `
        <img class="page-banner" src="/img/banners/shoutbox.webp" alt="" />
        <h1>The Shoutbox</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          The realm's open chamber — every voice, no censor. Be brief, be civil.
        </p>

        ${flash ? `<div class="banner banner-${flash.kind}">${flash.message}</div>` : ''}

        <div class="shoutbox-stream" id="shoutbox-stream" style="
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: 8px;
          padding: 0.75rem;
          max-height: 60vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        ">
          ${streamHtml()}
        </div>

        <form id="shoutbox-form" style="margin-top: 0.75rem; display: flex; gap: 0.5rem;">
          <input type="text" id="shoutbox-input" maxlength="280" autocomplete="off"
                 placeholder="${verified ? 'Speak your piece…' : 'Verify your email to post.'}"
                 ${verified ? '' : 'disabled'}
                 style="flex: 1; min-height: 44px; font-size: 16px;">
          <button class="btn btn-primary" type="submit" ${verified && !posting ? '' : 'disabled'}>
            ${posting ? 'Posting…' : 'Shout'}
          </button>
        </form>
        <p class="caption" style="margin-top: 0.4rem;">280 characters · refreshes every 10 seconds · 5 second cooldown between shouts</p>
      `;

      // Auto-scroll to bottom on render so newest shouts are visible.
      const stream = main.querySelector<HTMLDivElement>('#shoutbox-stream');
      if (stream) stream.scrollTop = stream.scrollHeight;

      const form = main.querySelector<HTMLFormElement>('#shoutbox-form');
      const input = main.querySelector<HTMLInputElement>('#shoutbox-input');
      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!verified || posting) return;
        const body = input?.value?.trim() ?? '';
        if (!body) return;
        posting = true;
        anchored(renderMain);
        try {
          await api.postShout(CHANNEL, body);
          if (input) input.value = '';
          await refresh();
          flash = null;
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not post.' };
        }
        posting = false;
        anchored(renderMain);
      });
    };

    anchored(renderMain);

    // Set up polling — clear any previous timer on re-mount.
    if (pollTimer !== null) window.clearInterval(pollTimer);
    pollTimer = window.setInterval(async () => {
      await refresh();
      renderStream(); // only the shouts list — leaves the post box (and what
                      // you're typing) completely untouched.
    }, REFRESH_MS);

    // Stop polling when this page is replaced.
    const onUnload = (): void => {
      if (pollTimer !== null) window.clearInterval(pollTimer);
      window.removeEventListener('hashchange', onUnload);
    };
    window.addEventListener('hashchange', onUnload, { once: true });
  });
}
